import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Leaf, Send, MessageCircle, Truck } from 'lucide-react';
import { companyInfo } from '../../data/mock';

// MAX icon component
const MaxIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm4 0h-2v-6h2v6zm-2-8c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
  </svg>
);

const Footer = () => {
  return (
    <footer className="bg-emerald-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Leaf className="w-6 h-6 text-emerald-300" />
              </div>
              <span className="text-lg font-bold">{companyInfo.name}</span>
            </div>
            <p className="text-emerald-200 text-sm leading-relaxed mb-4">
              Производство и продажа качественных теплиц из поликарбоната. 
              Собственное производство, гарантия качества.
            </p>
            <div className="flex items-center gap-2 text-emerald-300 text-sm mb-4">
              <Truck className="w-4 h-4" />
              <span>Доставка: {companyInfo.delivery.regions.join(" и ")}</span>
            </div>
            <div className="flex gap-2">
              <a 
                href={companyInfo.social.telegram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors"
                title="Telegram"
              >
                <Send className="w-4 h-4" />
              </a>
              <a 
                href={companyInfo.social.whatsapp} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors"
                title="WhatsApp"
              >
                <MessageCircle className="w-4 h-4" />
              </a>
              <a 
                href={companyInfo.social.max} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors"
                title="MAX"
              >
                <span className="text-xs font-bold">M</span>
              </a>
            </div>
          </div>

          {/* Catalog */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Каталог</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/catalog?category=standart" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Теплица Стандарт (от 20 000 ₽)
                </Link>
              </li>
              <li>
                <Link to="/catalog?category=standart-usilennaya" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Теплица Стандарт Усиленная (от 21 500 ₽)
                </Link>
              </li>
              <li>
                <Link to="/catalog?category=bogatyr" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Теплица Богатырь (от 23 500 ₽)
                </Link>
              </li>
              <li>
                <Link to="/catalog?category=premium" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Теплица Премиум (от 29 000 ₽)
                </Link>
              </li>
              <li>
                <Link to="/catalog?category=domikom" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Теплица Домиком (от 32 000 ₽)
                </Link>
              </li>
              <li>
                <Link to="/custom" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Фермерские теплицы
                </Link>
              </li>
            </ul>
          </div>

          {/* Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Информация</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/delivery" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Доставка
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Галерея работ
                </Link>
              </li>
              <li>
                <Link to="/reviews" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Отзывы клиентов
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Полезные статьи
                </Link>
              </li>
              <li>
                <Link to="/contacts" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Контакты
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-emerald-200 hover:text-white transition-colors text-sm">
                  Политика конфиденциальности
                </Link>
              </li>
            </ul>
          </div>

          {/* Contacts */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Контакты</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <a href={`tel:${companyInfo.phoneClean}`} className="block hover:text-emerald-300 transition-colors font-medium">
                    {companyInfo.phone}
                  </a>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <a 
                      href={companyInfo.social.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer" 
                      className="text-emerald-300 text-xs hover:text-white"
                    >
                      WhatsApp
                    </a>
                    <span className="text-emerald-500">|</span>
                    <a 
                      href={companyInfo.social.telegram}
                      target="_blank"
                      rel="noopener noreferrer" 
                      className="text-emerald-300 text-xs hover:text-white"
                    >
                      Telegram
                    </a>
                    <span className="text-emerald-500">|</span>
                    <a 
                      href={companyInfo.social.max}
                      target="_blank"
                      rel="noopener noreferrer" 
                      className="text-emerald-300 text-xs hover:text-white"
                    >
                      MAX
                    </a>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <a href={`mailto:${companyInfo.email}`} className="text-emerald-200 hover:text-white transition-colors text-sm">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-emerald-200 text-sm">{companyInfo.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-emerald-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-emerald-300 text-sm text-center md:text-left">
              <p>© 2026 {companyInfo.name}. Все права защищены.</p>
              <p className="text-emerald-400 text-xs mt-1">{companyInfo.owner}</p>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-emerald-300 hover:text-white transition-colors">
                Политика конфиденциальности
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
